import { create } from 'zustand';
import { LIMITES, type EntregaEmAndamento, type SessaoEntrega } from '@remoto/protocolo';
import { ROTAS } from '@remoto/shared';
import { chamar } from '../api';
import { mensagemDoErro } from '../erros';
import { limparAoSair } from '../estado/cache';
import { tamanhoLegivel } from '../formato';
import {
  enviarComProgresso,
  EnvioCancelado,
  EnvioSemRede,
} from '../componentes/chat/envio-com-progresso';
import { criarMedidor, type Progresso } from '../componentes/chat/progresso-do-envio';
import {
  aplicarFimDaEntrega,
  entregaDoItem,
  guardarFimAntecipado,
  iniciarEntrega,
  lerEntrega,
  lerFimDaEntrega,
  marcarSemConfirmacao,
  prazoSemConfirmacaoMs,
  removerJaVistas,
  type NaFila,
} from './fila-de-envio';

/**
 * Tudo o que a pessoa está mandando, FORA de qualquer componente, por conversa.
 *
 * ## Por que existe
 *
 * Com anexo de até 5 GiB o upload dura minutos, às vezes horas, e o que o
 * guardava era o `Compositor`: no celular ele só existe na aba `chat`
 * (`workspace/NoCelular.tsx`), então olhar a lista de sessões desmontava o
 * compositor e a limpeza do efeito abortava o XHR — um arquivo em 40% morria
 * porque a pessoa foi ver outra conversa. O mesmo valia para a falha de uma
 * entrega em segundo plano: o ouvinte do socket vivia dentro da conversa
 * aberta, e a troca de conversa zerava a fila — a falha chegava a ninguém.
 *
 * Aqui mora o que sobrevive a isso:
 *
 * - `anexos`: os chips do compositor de cada conversa, com o upload em curso;
 * - `mensagens`: a tira de espera de cada conversa, com as entregas em
 *   andamento, as que falharam e as que ficaram sem confirmação.
 *
 * ## O que vale
 *
 * - Trocar de conversa ou de aba NÃO aborta nada. O X do chip e o remover
 *   abortam; `sair()` aborta tudo e esvazia o store — estado de uma conta não
 *   sobrevive à troca de conta (`estado/cache.ts`).
 * - Recarregar a página perde tudo: o upload morre com ela e a entrega vive na
 *   memória da API. Dívida declarada; não há `beforeunload` (diálogo nativo).
 * - Quem ouve `sessao.entrega` é UM ouvinte global (`socket/painel.ts`), não
 *   um por conversa aberta.
 */

/** Um anexo na fila, do arquivo escolhido até a chave que a API devolveu. */
export interface AnexoNaFila {
  id: string;
  nome: string;
  mime: string;
  tamanho: number;
  /** Miniatura local; só existe para imagem pequena (`LIMITE_DA_PREVIA_BYTES`). */
  previa?: string;
  chave?: string;
  erro?: string;
  /**
   * Quanto já subiu. Ausente enquanto a API ainda prepara o envio (o `POST` que
   * devolve o endereço), e descartado quando o anexo termina.
   */
  progresso?: Progresso;
}

interface EstadoDeEnvios {
  anexos: Record<string, readonly AnexoNaFila[]>;
  mensagens: Record<string, readonly NaFila[]>;
}

export const usarEnvios = create<EstadoDeEnvios>(() => ({ anexos: {}, mensagens: {} }));

/**
 * A lista vazia é UMA só: um seletor que devolvesse `[]` novo a cada leitura
 * faria o Zustand ver "mudou" em todo render e o componente girar sem parar.
 */
export const SEM_ANEXOS: readonly AnexoNaFila[] = [];
export const SEM_MENSAGENS: readonly NaFila[] = [];

function mesmosItens<T>(a: readonly T[], b: readonly T[]): boolean {
  return a.length === b.length && a.every((item, i) => item === b[i]);
}

function mudarAnexos(
  sessaoId: string,
  mudar: (lista: readonly AnexoNaFila[]) => readonly AnexoNaFila[],
): void {
  usarEnvios.setState((e) => {
    const atual = e.anexos[sessaoId] ?? SEM_ANEXOS;
    const nova = mudar(atual);
    if (mesmosItens(atual, nova)) return e;
    const { [sessaoId]: _saiu, ...resto } = e.anexos;
    return { anexos: nova.length === 0 ? resto : { ...resto, [sessaoId]: nova } };
  });
}

function mudarMensagens(
  sessaoId: string,
  mudar: (lista: readonly NaFila[]) => readonly NaFila[],
): void {
  usarEnvios.setState((e) => {
    const atual = e.mensagens[sessaoId] ?? SEM_MENSAGENS;
    const nova = mudar(atual);
    if (mesmosItens(atual, nova)) return e;
    const { [sessaoId]: _saiu, ...resto } = e.mensagens;
    return { mensagens: nova.length === 0 ? resto : { ...resto, [sessaoId]: nova } };
  });
}

// ---------------------------------------------------------------------------
// Os anexos: do arquivo escolhido até a chave
// ---------------------------------------------------------------------------

/**
 * As frases do anexo que o catálogo geral diz de outro jeito.
 *
 * O link de envio vale 10 min e é conferido só no INÍCIO do `PUT`, então
 * `token_expirado` aqui quer dizer "demorou entre pedir o endereço e começar",
 * e a saída é anexar de novo — o "recarregue a página" do catálogo mandaria
 * perder a mensagem que a pessoa estava escrevendo.
 */
const FRASES_DO_ANEXO: Record<string, string> = {
  token_expirado: 'O endereço de envio expirou. Anexe o arquivo de novo.',
};

/**
 * Por que o anexo não subiu, em uma frase.
 *
 * A recusa da API diz o MOTIVO (tipo, tamanho, sessão, espaço): "não subiu"
 * sozinho mandava tentar de novo o que vai ser recusado de novo. Queda de rede
 * é o único caso em que tentar de novo é o conselho certo, e por isso tem frase
 * própria.
 */
export function motivoDoAnexo(falha: unknown): string {
  if (falha instanceof EnvioSemRede) return 'a conexão caiu durante o envio — anexe de novo';
  return mensagemDoErro(falha, 'não subiu', FRASES_DO_ANEXO);
}

/** O relógio da barra: sem ele, um envio travado mostraria para sempre o último ritmo bom. */
const TIQUE_DO_PROGRESSO_MS = 1_000;
/** A barra se redesenha 4 vezes por segundo: o navegador avisa umas 20, e a tela não precisa de tantas. */
const INTERVALO_DE_DESENHO_MS = 250;

/**
 * Acima disto o anexo de imagem não ganha miniatura.
 *
 * A miniatura é um `<img>` sobre o arquivo INTEIRO: um PNG ou TIFF de 300 MB
 * é decodificado inteiro para desenhar 32 px, e no celular isso derruba a
 * aba. A pessoa vê o ícone de arquivo, o nome e a barra — o que importa.
 */
export const LIMITE_DA_PREVIA_BYTES = 20 * 1024 * 1024;

/** O envio em andamento de um anexo: o que é preciso para interrompê-lo. */
interface EnvioDoAnexo {
  cancelado: boolean;
  /** Ausente até o `PUT` começar: antes disso o que há para parar é a espera pelo endereço. */
  abortar: (() => void) | null;
}

const emAndamento = new Map<string, EnvioDoAnexo>();

/**
 * O id de cada anexo, por contador de módulo.
 *
 * Era `${Date.now()}-${arquivo.name}`. O seletor de fotos do iPhone dá a TUDO o
 * nome `image.jpg`, e `receber` chama este módulo uma vez por arquivo no mesmo
 * milissegundo: dois chips com a mesma chave React, um XHR que nenhum botão
 * alcançava e a MESMA `chave` carimbada nos dois, porque quem atualiza o
 * chip casa pelo id. `crypto.randomUUID` não serve: o painel roda em
 * `http://<ip>:4173` na rede de casa, e lá o navegador o esconde (contexto
 * não seguro).
 */
let contadorDeAnexos = 0;

function soltarPrevia(anexo: AnexoNaFila): void {
  if (anexo.previa) URL.revokeObjectURL(anexo.previa);
}

/** Para um envio, esteja ele esperando o endereço ou já empurrando bytes. */
function interromper(id: string): void {
  const envio = emAndamento.get(id);
  if (!envio) return;
  envio.cancelado = true;
  envio.abortar?.();
  emAndamento.delete(id);
}

/**
 * Escolheu um arquivo: põe o chip na conversa e sobe.
 *
 * Devolve a promessa do envio só para o teste esperar; quem chama da tela não
 * precisa dela, e o chip é a única janela para o que acontece.
 */
export async function anexarArquivo(sessaoId: string, arquivo: File): Promise<void> {
  contadorDeAnexos += 1;
  const id = `anexo-${contadorDeAnexos}`;
  const mime = arquivo.type || 'application/octet-stream';
  const entrada: AnexoNaFila = {
    id,
    nome: arquivo.name,
    mime,
    tamanho: arquivo.size,
    ...(mime.startsWith('image/') && arquivo.size <= LIMITE_DA_PREVIA_BYTES
      ? { previa: URL.createObjectURL(arquivo) }
      : {}),
  };
  mudarAnexos(sessaoId, (lista) => [...lista, entrada]);

  const marcar = (mudanca: Partial<AnexoNaFila>): void =>
    mudarAnexos(sessaoId, (lista) => lista.map((a) => (a.id === id ? { ...a, ...mudanca } : a)));

  if (arquivo.size > LIMITES.anexoBytes) {
    marcar({ erro: `passa de ${tamanhoLegivel(LIMITES.anexoBytes)}` });
    return;
  }

  // Registrado ANTES do primeiro `await`: cancelar enquanto a API ainda
  // prepara o endereço tem de impedir o `PUT`, não só interrompê-lo.
  const envio: EnvioDoAnexo = { cancelado: false, abortar: null };
  emAndamento.set(id, envio);
  let relogio: ReturnType<typeof setInterval> | undefined;

  try {
    // O arquivo nunca passa pela API: ela só diz onde largar.
    const preparo = await chamar<{ chave: string; urlEnvio: string }>(
      `${ROTAS.sessoes}/${sessaoId}/anexos`,
      { method: 'POST', corpo: { mime, tamanho: arquivo.size } },
    );
    if (envio.cancelado) return;

    const medidor = criarMedidor(arquivo.size);
    let desenhadoEm = 0;
    const desenhar = (progresso: Progresso, forcar: boolean): void => {
      const agora = performance.now();
      if (!forcar && agora - desenhadoEm < INTERVALO_DE_DESENHO_MS) return;
      desenhadoEm = agora;
      marcar({ progresso });
    };
    desenhar(medidor.amostrar(0), true);
    relogio = setInterval(() => desenhar(medidor.amostrar(), true), TIQUE_DO_PROGRESSO_MS);

    const subida = enviarComProgresso(preparo.urlEnvio, arquivo, {
      mime,
      aoProgredir: (enviados) => desenhar(medidor.amostrar(enviados), false),
    });
    envio.abortar = subida.abortar;
    await subida.promessa;

    emAndamento.delete(id);
    marcar({ chave: preparo.chave, progresso: undefined });
  } catch (falha) {
    // Cancelar não é falha: o chip já saiu da fila, e uma frase de erro
    // aparecendo depois dele seria um fantasma.
    if (falha instanceof EnvioCancelado || envio.cancelado) return;
    emAndamento.delete(id);
    marcar({ erro: motivoDoAnexo(falha), progresso: undefined });
  } finally {
    clearInterval(relogio);
  }
}

/** O X do chip: interrompe, se estiver subindo, e tira da conversa. */
export function tirarAnexo(sessaoId: string, id: string): void {
  interromper(id);
  const anexo = (usarEnvios.getState().anexos[sessaoId] ?? SEM_ANEXOS).find((a) => a.id === id);
  if (anexo) soltarPrevia(anexo);
  mudarAnexos(sessaoId, (lista) => lista.filter((a) => a.id !== id));
}

/** A mensagem saiu com os anexos: os chips da conversa terminam aqui. */
export function descartarAnexos(sessaoId: string): void {
  for (const anexo of usarEnvios.getState().anexos[sessaoId] ?? SEM_ANEXOS) {
    interromper(anexo.id);
    soltarPrevia(anexo);
  }
  mudarAnexos(sessaoId, () => SEM_ANEXOS);
}

// ---------------------------------------------------------------------------
// As mensagens: a tira de espera e a entrega em segundo plano
// ---------------------------------------------------------------------------

/** Os prazos de "sem confirmação", por entrega. */
const prazos = new Map<string, ReturnType<typeof setTimeout>>();
/** Fins que chegaram antes da resposta do `POST` que os explica. */
let antecipados = new Map<string, SessaoEntrega>();
/**
 * Entregas cujo item a pessoa tirou da vista: se uma delas FALHAR depois, o
 * item volta — a mensagem não chegou, e isso não se esconde.
 */
let dispensadas = new Map<string, NaFila>();
const TETO_DE_DISPENSADAS = 20;

function desarmarPrazo(entregaId: string): void {
  const prazo = prazos.get(entregaId);
  if (prazo !== undefined) clearTimeout(prazo);
  prazos.delete(entregaId);
}

function armarPrazo(sessaoId: string, entrega: EntregaEmAndamento): void {
  desarmarPrazo(entrega.entregaId);
  prazos.set(
    entrega.entregaId,
    setTimeout(() => {
      prazos.delete(entrega.entregaId);
      mudarMensagens(sessaoId, (lista) => marcarSemConfirmacao(lista, entrega.entregaId));
    }, prazoSemConfirmacaoMs(entrega.bytes)),
  );
}

/**
 * O id de cada mensagem da tira: contador de módulo, como o do anexo. Era
 * `${Date.now()}-${texto.length}`, que repete para duas mensagens do mesmo
 * tamanho no mesmo milissegundo — e quem atualiza o item casa pelo id.
 */
let contadorDeMensagens = 0;
export function novoIdDeMensagem(): string {
  contadorDeMensagens += 1;
  return `mensagem-${contadorDeMensagens}`;
}

export function enfileirarMensagem(item: NaFila): void {
  mudarMensagens(item.sessaoId, (lista) => [...lista, item]);
}

/** A tentativa nova: o item sai do erro e do desfecho da anterior. */
export function reabrirMensagem(sessaoId: string, itemId: string): void {
  /*
    O desfecho some, mas o prazo da entrega antiga não pode ficar armado: ele
    marcaria "sem confirmação" numa tentativa que já é outra.
  */
  const antiga = (usarEnvios.getState().mensagens[sessaoId] ?? SEM_MENSAGENS).find(
    (m) => m.id === itemId,
  );
  const entregaId = antiga ? entregaDoItem(antiga) : undefined;
  if (entregaId !== undefined) desarmarPrazo(entregaId);
  mudarMensagens(sessaoId, (lista) =>
    lista.map((item) => {
      if (item.id !== itemId) return item;
      const { erro: _erro, desfecho: _desfecho, ...resto } = item;
      return resto;
    }),
  );
}

/** O `POST /enviar` recusou: a frase do código, no item. */
export function recusarEnvio(sessaoId: string, itemId: string, recado: string): void {
  mudarMensagens(sessaoId, (lista) =>
    lista.map((item) => (item.id === itemId ? { ...item, erro: recado, acordando: false } : item)),
  );
}

/**
 * O `POST /enviar` respondeu.
 *
 * Com `entrega`, a API aceitou a mensagem e a máquina ainda baixa os anexos: o
 * item vira "entregando", com prazo. O fim pode já ter chegado — o socket e o
 * `fetch` são caminhos distintos —, e então é aplicado agora.
 */
export function aceitarRespostaDoEnvio(sessaoId: string, itemId: string, resposta: unknown): void {
  const entrega = lerEntrega(resposta);
  const existe = (usarEnvios.getState().mensagens[sessaoId] ?? SEM_MENSAGENS).some(
    (m) => m.id === itemId,
  );
  mudarMensagens(sessaoId, (lista) => iniciarEntrega(lista, itemId, entrega));
  if (!entrega || !existe) return;

  const antecipado = antecipados.get(entrega.entregaId);
  if (antecipado && antecipado.sessaoId === sessaoId) {
    antecipados.delete(entrega.entregaId);
    mudarMensagens(sessaoId, (lista) => aplicarFimDaEntrega(lista, antecipado));
    return;
  }
  armarPrazo(sessaoId, entrega);
}

/**
 * Tira da vista — e, se ainda esperava uma entrega, lembra dela.
 *
 * É o escape de quem não quer esperar o prazo, e não pode custar a notícia de
 * que a mensagem não chegou.
 */
export function tirarMensagem(sessaoId: string, itemId: string): void {
  const item = (usarEnvios.getState().mensagens[sessaoId] ?? SEM_MENSAGENS).find(
    (m) => m.id === itemId,
  );
  if (item) {
    const entregaId = entregaDoItem(item);
    if (entregaId !== undefined && (item.entrega || item.desfecho?.tipo === 'sem-confirmacao')) {
      desarmarPrazo(entregaId);
      const novo = new Map(dispensadas);
      novo.delete(entregaId);
      novo.set(entregaId, item);
      while (novo.size > TETO_DE_DISPENSADAS) {
        const maisAntiga = novo.keys().next().value;
        if (maisAntiga === undefined) break;
        novo.delete(maisAntiga);
      }
      dispensadas = novo;
    }
  }
  mudarMensagens(sessaoId, (lista) => lista.filter((m) => m.id !== itemId));
}

/** O que já apareceu no transcript sai da tira. */
export function tirarMensagensJaVistas(sessaoId: string, ditas: readonly string[]): void {
  mudarMensagens(sessaoId, (lista) => removerJaVistas(lista, ditas));
}

/**
 * O `sessao.entrega` chegou. UM ouvinte global o entrega aqui — a conversa a
 * que ele pertence pode nem estar aberta.
 *
 * Só olha a lista da conversa do EVENTO: o evento vai à sala do usuário e chega
 * a todas as abas e conversas dele, e um `entregaId` de outra conversa nunca
 * pode mexer nesta.
 */
export function aoFimDaEntrega(carga: unknown): void {
  const fim = lerFimDaEntrega(carga);
  if (!fim) return;

  const lista = usarEnvios.getState().mensagens[fim.sessaoId] ?? SEM_MENSAGENS;
  if (lista.some((item) => entregaDoItem(item) === fim.entregaId)) {
    desarmarPrazo(fim.entregaId);
    mudarMensagens(fim.sessaoId, (atual) => aplicarFimDaEntrega(atual, fim));
    return;
  }

  const dispensada = dispensadas.get(fim.entregaId);
  if (dispensada && dispensada.sessaoId === fim.sessaoId) {
    dispensadas.delete(fim.entregaId);
    // A FALHA de uma mensagem que a pessoa tirou da vista volta à tira; o
    // sucesso não tem o que mostrar, e o desfecho desconhecido
    // (`entrega_sem_confirmacao`) tampouco: ela já tinha decidido não olhar.
    const voltou = aplicarFimDaEntrega([dispensada], fim);
    if (voltou[0]?.desfecho?.tipo === 'falhou') {
      mudarMensagens(fim.sessaoId, (atual) => [...atual, ...voltou]);
    }
    return;
  }

  // Sem dono ainda: ou a resposta do `POST` não chegou, ou é de outra aba.
  antecipados = guardarFimAntecipado(antecipados, fim);
}

/**
 * O socket voltou: o que esperava uma entrega perdeu a garantia de ouvir o fim.
 *
 * O evento pode ter saído enquanto o socket estava fora, e a API não o repete.
 * O item deixa de dizer "recebendo" e passa a dizer que não sabe — sem "tentar
 * de novo", porque a mensagem pode ter chegado. Se o fim ainda vier, vale mais
 * que esta dúvida (`aplicarFimDaEntrega` casa também o item em "sem
 * confirmação").
 */
export function aoSocketReconectar(): void {
  for (const sessaoId of Object.keys(usarEnvios.getState().mensagens)) {
    for (const item of usarEnvios.getState().mensagens[sessaoId] ?? SEM_MENSAGENS) {
      if (item.entrega) desarmarPrazo(item.entrega.entregaId);
    }
    mudarMensagens(sessaoId, (lista) => marcarSemConfirmacao(lista));
  }
}

/**
 * Aborta tudo o que sobe e esvazia o store: o que o `sair()` pede.
 *
 * Também serve ao teste, para cada caso começar do zero.
 */
export function encerrarEnvios(): void {
  for (const envio of emAndamento.values()) {
    envio.cancelado = true;
    envio.abortar?.();
  }
  emAndamento.clear();
  for (const lista of Object.values(usarEnvios.getState().anexos)) {
    for (const anexo of lista) soltarPrevia(anexo);
  }
  for (const prazo of prazos.values()) clearTimeout(prazo);
  prazos.clear();
  antecipados = new Map();
  dispensadas = new Map();
  usarEnvios.setState({ anexos: {}, mensagens: {} });
}

limparAoSair(encerrarEnvios);
